import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GlobalServicesService } from 'src/app/services/global-services.service';
import { FormBuilderService } from 'src/app/services/form-builder.service';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbService   } from 'xng-breadcrumb';
import { Subject, takeUntil } from 'rxjs';
import { FileUploadService } from 'src/app/services/file-upload.service';


interface FormItem {
    uuid: string;
    name: string;
    label: string;
    is_editable: boolean;
    created_at: string;
}

@Component({
    selector: 'app-form-list',
    templateUrl: './form-list.component.html',
    styleUrls: ['./form-list.component.css'],
    standalone: false
})
export class FormsListComponent implements OnInit, OnDestroy {
    unsub$: Subject<void> = new Subject<void>();
    categories = ['registration', 'feedback', 'documentation', 'offer'];
    activeCategory = 'registration';
    forms: FormItem[] = [];
    isLoading = false;
    userPrivileges: Set<string> = new Set();

    categoriesList: any = [
        { label: 'Registration', value: 'registration' },
        { label: 'Feedback', value: 'feedback' },
        { label: 'Documentation', value: 'documentation' },
        { label: 'Offer', value: 'offer' }
    ];

    @Input() createNewForm$: Subject<void> = new Subject<void>();
    createNewForm: boolean = false;
    combineFormat: any;
    formSettings = {
        category: "",
        formName: '',
        logoImage: '',
        coverImage: '',
        logoPreview: '',
        coverPreview: '',
        touched: false,
    };
    uploadingLogo: boolean = false;
    uploadingCover: boolean = false;

    constructor(
        private globalService: GlobalServicesService,
        private formBuilderService: FormBuilderService,
        private messageService: MessageService,
        private breadcrumbService: BreadcrumbService  ,
        private router: Router,
        private route: ActivatedRoute,
        private fileUploadService: FileUploadService
    ) {
        const dateFormat = localStorage.getItem('company-details') ? JSON.parse(localStorage.getItem('company-details') || '{}').company_settings?.date_format : '';
        const timeFormat = localStorage.getItem('company-details') ? JSON.parse(localStorage.getItem('company-details') || '{}').company_settings?.time_format : '';
        this.combineFormat = timeFormat == 'hh:mm' ? dateFormat  + '  '+ timeFormat + ' a' : dateFormat  + '  '+ timeFormat;
     }

    ngOnInit() {
        const initialCategory = this.route.snapshot.queryParamMap.get('category');
        if (initialCategory && this.categories.includes(initialCategory)) {
            this.activeCategory = initialCategory;
        }
        
        this.fetchForms(this.activeCategory);

        this.globalService.privilegeDetailsSubject.subscribe((privileges) => {
            this.userPrivileges = privileges;
        });

        this.createNewForm$.pipe(takeUntil(this.unsub$)).subscribe((res) => {
            this.formSettings = {
                category: this.activeCategory,
                formName: '',
                logoImage: '',
                coverImage: '',
                logoPreview: '',
                coverPreview: '',
                touched: false
            };
            this.createNewForm = true;
            // this.createForm(this.activeCategory);
        })
    }

    ngOnDestroy(): void {
        this.unsub$.next();
        this.unsub$.complete();
    }


    fetchForms(category: string) {
        this.isLoading = true;
        this.activeCategory = category;
        const currentMenu = this.route.snapshot.queryParamMap.get('menu');
        if (currentMenu === 'form-builder') {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { menu: 'form-builder', category: category },
                replaceUrl: true
            });
        }
    
        this.formBuilderService.listFormsFastApi(this.activeCategory).subscribe({
            next: (response: any) => {
                this.forms = response.body;
                this.isLoading = false;
            },
            error: () => {
                this.forms = [];
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch forms. Please try again.'
                });
                this.isLoading = false;
            }
        });
    }

    onDelete(form: FormItem) {
        if (form.is_editable) {
            this.formBuilderService.deleteFormFastApi(form.uuid).subscribe({
                next: (response) => {
                    this.messageService.add({severity: 'info', summary: 'Deleted', detail: 'Form deleted successfully'});
                    this.fetchForms(this.activeCategory);
                },
                error: (error) => {
                    this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to delete form'});
                }
            });
        }
    }

    onEdit(form: FormItem) {
        if (form.is_editable) {
            this.router.navigate(["/forms/create"], {
                queryParams: {
                    category: this.activeCategory,
                    uuid: form.uuid,
                },
            });
        } else {
            this.messageService.add({
                severity: "warn",
                summary: "Cannot Edit",
                detail: "This form is not editable",
            });
        }
    }
    

    onView(form: FormItem) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/forms/view?uuid=${form.uuid}`;
        window.open(url, '_blank');
    }

    createForm() {
        this.formSettings.touched = true;
        
        const missingFields: string[] = [];
        if (!this.formSettings.formName) {
            missingFields.push('form name');
        }
        if (!this.formSettings.logoImage || !this.formSettings.logoPreview) {
            missingFields.push('form logo');
        }
        if (missingFields.length) {
            const formattedList = missingFields.join(' and ');
            this.messageService.add({
                severity: 'warn',
                summary: 'Missing Required Details',
                detail: `The ${formattedList} ${missingFields.length === 1 ? 'is' : 'are'} missing. Please fill them in before proceeding.`,
            });
            return;
        }

        this.formBuilderService.formSettings.next(this.formSettings);
        this.router.navigate(['/forms/create'], { queryParams: { category: this.formSettings.category } });
    }
    onClone(form: FormItem) {
        this.router.navigate(['/forms/create'], {
          queryParams: {
            category: this.activeCategory,
            cloneUuid: form.uuid 
          }
        });
      }

      onFileSelected(event: any, type: 'logo' | 'cover') {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            this.messageService.add({
                severity: 'error',
                summary: 'Invalid File',
                detail: 'Please upload only JPG, JPEG or PNG files'
            });
            return;
        }

        const maxSize = type === 'logo' ? 500 * 1024 : 2 * 1024 * 1024;
        if (file.size > maxSize) {
            this.messageService.add({
                severity: 'error',
                summary: 'File Too Large',
                detail: `${type === 'logo' ? 'Logo' : 'Cover'} image must be less than ${type === 'logo' ? '500KB' : '2MB'}`
            });
            return;
        }

        const fileName = this.generateFileName(file);
        const renamedFile = new File([file], fileName, {
            type: file.type,
            lastModified: file.lastModified,
        });

        this.handleImageUpload(renamedFile, fileName, type);
    }

    private generateFileName(file: any): string {
        let fileName = file.name.split(".");
        let fileExtension = fileName.pop();
        const timestamp = new Date().getTime();
        return `${fileName.join().replace(/\s/g, "").replace(/[^\w\s]/gi, '')}_${timestamp}.${fileExtension}`;
    }

    private handleImageUpload(file: File, fileName: string, type: 'logo' | 'cover') {
        if (type === 'logo') {
            this.uploadingLogo = true;
        } else {
            this.uploadingCover = true;
        }

        const filePath = `form-images/${type}/${fileName}`;
        const payload = {
            file_path: filePath,
            bucket_type: 'public',
            origin:"form_builder"
        };

        this.fileUploadService.getSignedUrlGCS(payload, false).subscribe({
            next: (response: any) => {
                if (response?.body?.signed_url) {
                    // this.bucketUrl = response?.body?.signed_url.split(fileName)[0];
                    this.uploadFileToGCS(response.body.signed_url, file, filePath, fileName, type);
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Invalid signed URL received'
                    });
                    this.resetUploadState(type);
                }
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error getting signed URL'
                });
                this.resetUploadState(type);
            }
        });
    }

    private uploadFileToGCS(signedUrl: string, file: File, filePath: string, fileName: string, type: 'logo' | 'cover') {
        this.fileUploadService.uploadUsingSignedUrlGCS(signedUrl, file).subscribe({
            next: (response: any) => {
                if (response?.status === "success") {
                    const reader = new FileReader();
                    reader.onload = (e: any) => {
                        if (type === 'logo') {
                            this.formSettings.logoPreview = e.target.result;
                            this.formSettings.logoImage = fileName;
                        } else {
                            this.formSettings.coverPreview = e.target.result;
                            this.formSettings.coverImage = fileName;
                        }
                        // this.updateFormStateWithDebounce();
                    };
                    reader.readAsDataURL(file);

                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${type === 'logo' ? 'Logo' : 'Cover'} image uploaded successfully`
                    });
                    this.resetUploadState(type);
                }
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error uploading file'
                });
                this.resetUploadState(type);
            }
        });
    }

    private resetUploadState(type: 'logo' | 'cover') {
        if (type === 'logo') {
            this.uploadingLogo = false;
        } else {
            this.uploadingCover = false;
        }
    }
}
